import { useEffect, useRef } from 'react';
import type { JSX } from 'react';

export interface ContextMenuProps {
  readonly x: number;
  readonly y: number;
  readonly hasBrick: boolean;
  readonly onClose: () => void;
  readonly onAdd: () => void;
  readonly onToggle: () => void;
  readonly onSelectOnly: () => void;
  readonly onDelete: () => void;
  readonly snapEnabled: boolean;
  readonly onToggleSnap: () => void;
}

/**
 * Design document C7 — on touch there are no modifier keys, so a long press
 * offers the operations Shift, Ctrl and Alt serve on a desktop.
 */
export function CanvasContextMenu(props: ContextMenuProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ref.current?.querySelector('button')?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') props.onClose();
    };
    const onDown = (event: PointerEvent): void => {
      if (ref.current !== null && !ref.current.contains(event.target as Node)) props.onClose();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onDown, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onDown, true);
    };
  }, [props]);

  const act = (run: () => void) => () => {
    run();
    props.onClose();
  };

  return (
    <div
      className="ctxmenu"
      role="menu"
      aria-label="Canvas actions"
      ref={ref}
      style={{ left: Math.max(6, props.x - 20), top: Math.max(6, props.y - 10) }}
    >
      <button role="menuitem" disabled={!props.hasBrick} onClick={act(props.onSelectOnly)}>
        Select this brick
      </button>
      <button role="menuitem" disabled={!props.hasBrick} onClick={act(props.onAdd)}>
        Add to selection
      </button>
      <button role="menuitem" disabled={!props.hasBrick} onClick={act(props.onToggle)}>
        Toggle in selection
      </button>
      <hr />
      <button role="menuitem" onClick={act(props.onToggleSnap)}>
        {props.snapEnabled ? 'Turn smart snap off' : 'Turn smart snap on'}
      </button>
      <button role="menuitem" disabled={!props.hasBrick} onClick={act(props.onDelete)}>
        Delete selection
      </button>
    </div>
  );
}
